import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import Button from '../components/Button';
import DesignerOnboarding from '../components/DesignerOnboarding';
import AnalyticsChart from '../components/AnalyticsChart';
import ShareModal from '../components/ShareModal';
import { 
  fetchDashboardTemplates, 
  fetchDashboardAnalytics, 
  updateTemplateStatus, 
  boostTemplate 
} from '../api/client';
import { 
  TrendingUp, 
  DollarSign, 
  Download, 
  Eye, 
  Plus, 
  ArrowUpRight, 
  MoreVertical, 
  Filter, 
  Search, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  ExternalLink,
  Sparkles,
  Layers,
  Heart,
  Star,
  Share2,
  Zap,
  X
} from 'lucide-react';

// Fallback demo assets for when creator hasn't uploaded yet
const mockTemplates = [
  {
    id: 't-1',
    title: 'Glassmorphism UI Kit Pro',
    category: 'UI/UX Design',
    formats: ['FIG', 'PSD'],
    price: 24,
    status: 'published',
    downloads: 482,
    views: 3240,
    revenue: 8676,
    rating: 4.9,
    publishedAt: '2026-06-12',
    thumbnail: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=400&q=80'
  },
  {
    id: 't-2',
    title: 'Cyberpunk 3D Hologram Pack',
    category: '3D Graphics',
    formats: ['BLENDER', 'OBJ', 'PNG'],
    price: 35,
    status: 'published',
    downloads: 310,
    views: 2890,
    revenue: 7595,
    rating: 4.8,
    publishedAt: '2026-07-01',
    thumbnail: 'https://images.unsplash.com/photo-1634017839464-5c339ebe3cb4?w=400&q=80'
  },
  {
    id: 't-3',
    title: 'Minimalist Branding Mockups Vol. 2',
    category: 'Branding',
    formats: ['PSD'],
    price: 0,
    status: 'published',
    downloads: 1250,
    views: 6420,
    revenue: 0,
    rating: 4.95,
    publishedAt: '2026-07-15',
    thumbnail: 'https://images.unsplash.com/photo-1600132806370-bf17e65e942f?w=400&q=80'
  },
  {
    id: 't-4',
    title: 'Neo-Brutalism Social Media Kit',
    category: 'Social Media',
    formats: ['FIG', 'AI'],
    price: 18,
    status: 'draft',
    downloads: 0,
    views: 45,
    revenue: 0,
    rating: 0,
    publishedAt: '2026-08-10',
    thumbnail: 'https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=400&q=80'
  }
];

const mockRecentActivity = [
  { id: 1, type: 'sale', user: 'Alex Rivera', item: 'Glassmorphism UI Kit Pro', amount: '+$19.20', time: '12m ago' },
  { id: 2, type: 'download', user: 'Sarah Lin', item: 'Minimalist Branding Mockups', amount: 'Free', time: '45m ago' },
  { id: 3, type: 'rating', user: 'David Kim', item: 'Cyberpunk 3D Hologram Pack', rating: 5, time: '2h ago' },
  { id: 4, type: 'like', user: 'Elena Rostova', item: 'Glassmorphism UI Kit Pro', time: '3h ago' }
];

export default function Dashboard() {
  const { user, dbUser } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);

  // Boost Modal State
  const [boostModalOpen, setBoostModalOpen] = useState(false);
  const [boostingTemplate, setBoostingTemplate] = useState(null);
  const [boostSlot, setBoostSlot] = useState('hero');
  const [boostDuration, setBoostDuration] = useState(7);
  const [boosting, setBoosting] = useState(false);

  // Live Query: Designer's Own Templates
  const { data: templatesData, isLoading: loadingTemplates } = useQuery({
    queryKey: ['dashboard-templates'],
    queryFn: fetchDashboardTemplates,
    enabled: !!user,
  });

  // Live Query: Designer Analytics & Sales
  const { data: analyticsData, isLoading: loadingAnalytics } = useQuery({
    queryKey: ['dashboard-analytics'],
    queryFn: fetchDashboardAnalytics,
    enabled: !!user,
  });

  const liveTemplates = templatesData?.templates;
  const isUsingLiveTemplates = Array.isArray(liveTemplates) && liveTemplates.length > 0;
  const templates = isUsingLiveTemplates ? liveTemplates : mockTemplates;

  const liveStats = analyticsData?.stats;
  const liveRecentActivity = analyticsData?.recentActivity;
  const recentActivity = (Array.isArray(liveRecentActivity) && liveRecentActivity.length > 0)
    ? liveRecentActivity.map((r, idx) => ({
        id: r.id || idx,
        type: r.is_free ? 'download' : 'sale',
        user: r.buyer_name || 'Collector',
        item: r.template_title || 'Design Asset',
        amount: r.is_free ? 'Free' : `+$${((r.price_cents || 0) / 100 * 0.85).toFixed(2)}`,
        time: r.created_at ? new Date(r.created_at).toLocaleDateString() : 'Recently'
      }))
    : mockRecentActivity;

  // Handle Quick Status Toggle
  const handleStatusChange = async (templateId, newStatus) => {
    try {
      await updateTemplateStatus(templateId, newStatus);
      setToastMessage(`✓ Template status updated to ${newStatus}`);
      queryClient.invalidateQueries({ queryKey: ['dashboard-templates'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-analytics'] });
    } catch (err) {
      setToastMessage(err.message || 'Failed to update status');
    } finally {
      setTimeout(() => setToastMessage(null), 3000);
    }
  };

  // Handle Boost Submission
  const handleApplyBoost = async (e) => {
    e.preventDefault();
    if (!boostingTemplate) return;
    setBoosting(true);
    try {
      await boostTemplate({
        template_id: boostingTemplate.id,
        slot_type: boostSlot,
        duration_days: Number(boostDuration)
      });
      setBoostModalOpen(false);
      setToastMessage(`🚀 "${boostingTemplate.title}" is now boosted in ${boostSlot.toUpperCase()} slot!`);
      queryClient.invalidateQueries({ queryKey: ['featured-templates'] });
    } catch (err) {
      setToastMessage(err.message || 'Failed to boost template');
    } finally {
      setBoosting(false);
      setTimeout(() => setToastMessage(null), 4000);
    }
  };

  // Auth requirement check
  if (user === undefined || user === null) {
    return (
      <div className="page-container" style={{ padding: '100px 0', textAlign: 'center' }}>
        <div style={{
          maxWidth: '460px',
          margin: '0 auto',
          padding: '48px 32px',
          background: 'var(--bg-surface)',
          borderRadius: '24px',
          border: '1px solid var(--border-subtle)',
          boxShadow: 'var(--shadow-lg)'
        }}>
          <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: 'var(--primary-subtle)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <AlertCircle size={28} />
          </div>
          <h2 className="text-h2" style={{ marginBottom: '12px' }}>Authentication Required</h2>
          <p className="text-secondary" style={{ marginBottom: '28px', fontSize: '0.9375rem' }}>
            Please log in or create a creator account to access your studio command center.
          </p>
          <Button variant="primary" style={{ width: '100%' }} onClick={() => navigate('/browse')}>
            Browse Marketplace
          </Button>
        </div>
      </div>
    );
  }

  const isDesigner = dbUser?.role === 'designer' || true; // Designer mode

  // Filter templates
  const filteredTemplates = templates.filter(t => {
    const matchesSearch = t.title.toLowerCase().includes(searchQuery.toLowerCase()) || t.category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || t.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalEarnings = liveStats ? Math.round((liveStats.gross_revenue_cents || 0) / 100 * 0.85) : templates.reduce((acc, curr) => acc + curr.revenue, 0);
  const totalDownloads = liveStats ? (liveStats.total_downloads || 0) : templates.reduce((acc, curr) => acc + curr.downloads, 0);
  const totalViews = liveStats ? (liveStats.total_views || 0) : templates.reduce((acc, curr) => acc + curr.views, 0);
  const overallConversion = totalViews > 0 ? ((totalDownloads / totalViews) * 100).toFixed(1) : 0;

  return (
    <div className="page-container" style={{ paddingBottom: '100px', paddingTop: '32px' }}>
      
      {/* Toast Notification */}
      {toastMessage && (
        <div style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          background: 'var(--bg-surface-active)',
          border: '1px solid var(--primary)',
          color: '#FFFFFF',
          padding: '12px 20px',
          borderRadius: '14px',
          boxShadow: 'var(--shadow-lg)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          animation: 'fadeIn 0.2s ease'
        }}>
          <Sparkles size={16} color="var(--accent)" />
          <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>{toastMessage}</span>
        </div>
      )}

      {/* Top Studio Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <h1 className="text-h1">Creator Studio</h1>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              background: 'var(--primary-subtle)',
              color: 'var(--primary-text)',
              fontSize: '0.75rem',
              fontWeight: 700,
              padding: '3px 10px',
              borderRadius: '999px',
              border: '1px solid var(--primary-border)'
            }}>
              <Sparkles size={12} /> Verified Designer
            </span>
          </div>
          <p className="text-secondary" style={{ fontSize: '0.9375rem' }}>
            Manage your digital asset portfolio, monitor live engagement, and track earnings.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Button variant="secondary" onClick={() => navigate('/library')}>
            My Downloads
          </Button>
          <Button variant="primary" onClick={() => navigate('/dashboard/upload')}>
            <Plus size={16} /> New Template
          </Button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        borderBottom: '1px solid var(--border-subtle)',
        marginBottom: '32px',
        overflowX: 'auto'
      }}>
        {[
          { id: 'overview', label: 'Performance & Analytics' },
          { id: 'templates', label: `Asset Inventory (${templates.length})` },
          { id: 'payouts', label: 'Payouts & Balance' },
          { id: 'activity', label: 'Live Activity Feed' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '12px 18px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab.id ? '2px solid var(--primary)' : '2px solid transparent',
              color: activeTab === tab.id ? 'var(--text-primary)' : 'var(--text-secondary)',
              fontWeight: activeTab === tab.id ? 700 : 500,
              fontSize: '0.9375rem',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.15s ease'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* KPI Cards Banner */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '20px',
        marginBottom: '32px'
      }}>
        {/* Metric 1 */}
        <div style={{
          background: 'var(--bg-surface)',
          borderRadius: '20px',
          padding: '24px',
          border: '1px solid var(--border-subtle)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Gross Revenue</span>
            <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'var(--primary-subtle)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <DollarSign size={18} />
            </div>
          </div>
          <div style={{ fontSize: '1.85rem', fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.02em', marginBottom: '8px' }}>
            ${totalEarnings.toLocaleString()}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8125rem', color: 'var(--success)', fontWeight: 600 }}>
            <TrendingUp size={14} /> +24.8% vs previous month
          </div>
        </div>

        {/* Metric 2 */}
        <div style={{
          background: 'var(--bg-surface)',
          borderRadius: '20px',
          padding: '24px',
          border: '1px solid var(--border-subtle)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Total Downloads</span>
            <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'var(--accent-subtle)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Download size={18} />
            </div>
          </div>
          <div style={{ fontSize: '1.85rem', fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.02em', marginBottom: '8px' }}>
            {totalDownloads.toLocaleString()}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8125rem', color: 'var(--success)', fontWeight: 600 }}>
            <TrendingUp size={14} /> +18.2% new collectors
          </div>
        </div>

        {/* Metric 3 */}
        <div style={{
          background: 'var(--bg-surface)',
          borderRadius: '20px',
          padding: '24px',
          border: '1px solid var(--border-subtle)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Impressions & Views</span>
            <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'var(--info-subtle)', color: 'var(--info)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Eye size={18} />
            </div>
          </div>
          <div style={{ fontSize: '1.85rem', fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.02em', marginBottom: '8px' }}>
            {totalViews.toLocaleString()}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
            Across {templates.length} active listings
          </div>
        </div>

        {/* Metric 4 */}
        <div style={{
          background: 'var(--bg-surface)',
          borderRadius: '20px',
          padding: '24px',
          border: '1px solid var(--border-subtle)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Avg. Conversion</span>
            <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'var(--warning-subtle)', color: 'var(--warning)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Sparkles size={18} />
            </div>
          </div>
          <div style={{ fontSize: '1.85rem', fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.02em', marginBottom: '8px' }}>
            {overallConversion}%
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8125rem', color: 'var(--success)', fontWeight: 600 }}>
            Top 5% creator tier
          </div>
        </div>
      </div>

      {/* Tab 1: Overview & Charts */}
      {activeTab === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
          {/* Main SVG Analytics Component */}
          <AnalyticsChart title="Asset Performance & Earnings Trends" />

          {/* Quick Insights Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px' }}>
            {/* Top performing assets */}
            <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: '20px', padding: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700 }}>Top Performing Assets</h3>
                <button onClick={() => setActiveTab('templates')} style={{ background: 'none', border: 'none', color: 'var(--primary-text)', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  View All <ArrowUpRight size={14} />
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {templates.slice(0, 3).map((item, idx) => (
                  <div key={item.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: 'var(--bg-base)', borderRadius: '14px', border: '1px solid var(--border-subtle)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <img src={item.thumbnail} alt={item.title} style={{ width: '48px', height: '48px', borderRadius: '10px', objectFit: 'cover' }} />
                      <div>
                        <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--text-primary)' }}>{item.title}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{item.downloads} downloads · {item.rating > 0 ? `★ ${item.rating}` : 'New'}</div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '0.9375rem', fontWeight: 800, color: 'var(--text-primary)' }}>${item.revenue}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--accent)', fontWeight: 600 }}>Active</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Live Activity Stream */}
            <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: '20px', padding: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700 }}>Real-Time Activity</h3>
                <span style={{ fontSize: '0.75rem', color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--accent)', display: 'inline-block' }} /> Live
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {recentActivity.map(act => (
                  <div key={act.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: 'var(--bg-base)', borderRadius: '14px', border: '1px solid var(--border-subtle)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background: act.type === 'sale' ? 'var(--primary-subtle)' : act.type === 'download' ? 'var(--accent-subtle)' : 'var(--warning-subtle)',
                        color: act.type === 'sale' ? 'var(--primary)' : act.type === 'download' ? 'var(--accent)' : 'var(--warning)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {act.type === 'sale' ? <DollarSign size={15} /> : act.type === 'download' ? <Download size={15} /> : <Star size={15} />}
                      </div>
                      <div>
                        <div style={{ fontSize: '0.8125rem', color: 'var(--text-primary)' }}>
                          <strong>{act.user}</strong> {act.type === 'sale' ? 'purchased' : act.type === 'download' ? 'downloaded' : 'rated'} <span style={{ color: 'var(--text-secondary)' }}>{act.item}</span>
                        </div>
                        <div style={{ fontSize: '0.6875rem', color: 'var(--text-tertiary)' }}>{act.time}</div>
                      </div>
                    </div>
                    {act.amount && (
                      <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: act.amount.startsWith('+') ? 'var(--success)' : 'var(--text-secondary)' }}>
                        {act.amount}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab 2: Asset Inventory Data Table */}
      {activeTab === 'templates' && (
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: '20px', padding: '24px' }}>
          {/* Table Filters & Search */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, maxWidth: '400px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                background: 'var(--bg-base)',
                border: '1px solid var(--border-subtle)',
                borderRadius: '12px',
                padding: '8px 14px',
                width: '100%',
                gap: '8px'
              }}>
                <Search size={16} color="var(--text-tertiary)" />
                <input
                  type="text"
                  placeholder="Filter templates by name or category..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-primary)',
                    outline: 'none',
                    fontSize: '0.875rem',
                    width: '100%'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {['all', 'active', 'draft'].map(st => (
                <button
                  key={st}
                  onClick={() => setStatusFilter(st)}
                  style={{
                    padding: '6px 14px',
                    borderRadius: '8px',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    textTransform: 'capitalize',
                    background: statusFilter === st ? 'var(--bg-surface-active)' : 'transparent',
                    color: statusFilter === st ? 'var(--text-primary)' : 'var(--text-secondary)',
                    border: `1px solid ${statusFilter === st ? 'var(--border-strong)' : 'var(--border-subtle)'}`,
                    cursor: 'pointer'
                  }}
                >
                  {st}
                </button>
              ))}
            </div>
          </div>

          {/* Modern Data Table */}
          <div className="modern-table-container">
            <table className="modern-table">
              <thead>
                <tr>
                  <th>Template Name</th>
                  <th>Category</th>
                  <th>Formats</th>
                  <th>Price</th>
                  <th>Downloads</th>
                  <th>Gross Revenue</th>
                  <th>Rating</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTemplates.map(t => (
                  <tr key={t.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <img src={t.thumbnail} alt={t.title} style={{ width: '44px', height: '44px', borderRadius: '8px', objectFit: 'cover' }} />
                        <div>
                          <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{t.title}</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>Added on {t.publishedAt}</div>
                        </div>
                      </div>
                    </td>
                    <td>{t.category}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        {t.formats.map(f => (
                          <span key={f} style={{ fontSize: '0.6875rem', fontWeight: 700, padding: '2px 6px', background: 'var(--bg-base)', borderRadius: '4px', border: '1px solid var(--border-subtle)' }}>
                            {f}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td><strong>{t.price === 0 ? 'Free' : `$${t.price}`}</strong></td>
                    <td>{t.downloads.toLocaleString()}</td>
                    <td><strong style={{ color: 'var(--primary-text)' }}>${t.revenue.toLocaleString()}</strong></td>
                    <td>{t.rating > 0 ? `★ ${t.rating}` : '—'}</td>
                    <td>
                      <select
                        value={t.status === 'active' ? 'published' : t.status}
                        onChange={(e) => handleStatusChange(t.id, e.target.value)}
                        style={{
                          padding: '4px 8px',
                          borderRadius: '8px',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          background: (t.status === 'active' || t.status === 'published') ? 'var(--success-subtle)' : 'var(--warning-subtle)',
                          color: (t.status === 'active' || t.status === 'published') ? 'var(--success)' : 'var(--warning)',
                          border: '1px solid var(--border-subtle)',
                          cursor: 'pointer',
                          outline: 'none'
                        }}
                      >
                        <option value="published" style={{ background: 'var(--bg-surface)', color: 'var(--text-primary)' }}>Published</option>
                        <option value="draft" style={{ background: 'var(--bg-surface)', color: 'var(--text-primary)' }}>Draft</option>
                        <option value="archived" style={{ background: 'var(--bg-surface)', color: 'var(--text-primary)' }}>Archived</option>
                      </select>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px' }}>
                        <button
                          title="Boost Asset Placement"
                          onClick={() => { setBoostingTemplate(t); setBoostModalOpen(true); }}
                          className="tactile-btn"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '6px 10px',
                            borderRadius: '8px',
                            background: 'var(--primary-subtle)',
                            border: '1px solid var(--primary-border)',
                            color: 'var(--primary-text)',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            cursor: 'pointer'
                          }}
                        >
                          <Zap size={13} /> Boost
                        </button>
                        <button
                          title="Share"
                          onClick={() => { setSelectedTemplate(t); setShareModalOpen(true); }}
                          className="tactile-btn"
                          style={{ padding: '6px', borderRadius: '8px', background: 'var(--bg-base)', border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)', cursor: 'pointer' }}
                        >
                          <Share2 size={14} />
                        </button>
                        <button
                          title="Preview Live"
                          onClick={() => navigate(`/template/${t.slug || t.id}`)}
                          className="tactile-btn"
                          style={{ padding: '6px', borderRadius: '8px', background: 'var(--bg-base)', border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)', cursor: 'pointer' }}
                        >
                          <ExternalLink size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab 3: Payouts & Balance */}
      {activeTab === 'payouts' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px' }}>
          <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: '20px', padding: '28px' }}>
            <h3 style={{ fontSize: '1.2rem', fontWeight: 800, marginBottom: '8px' }}>Available Balance</h3>
            <div style={{ fontSize: '2.5rem', fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.02em', marginBottom: '16px' }}>
              $1,248.50
            </div>
            <p className="text-secondary" style={{ fontSize: '0.875rem', marginBottom: '24px' }}>
              Funds are automatically disbursed to your verified bank account on the 1st and 15th of every month.
            </p>

            {/* Threshold bar */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem', marginBottom: '6px' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Minimum Payout Threshold ($50.00)</span>
                <span style={{ fontWeight: 700, color: 'var(--success)' }}>100% Eligible</span>
              </div>
              <div style={{ height: '8px', background: 'var(--bg-base)', borderRadius: '999px', overflow: 'hidden' }}>
                <div style={{ width: '100%', height: '100%', background: 'linear-gradient(90deg, var(--primary), var(--accent))' }} />
              </div>
            </div>

            <Button variant="primary" style={{ width: '100%' }}>
              Request Instant Payout
            </Button>
          </div>

          <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: '20px', padding: '28px' }}>
            <h3 style={{ fontSize: '1.2rem', fontWeight: 800, marginBottom: '16px' }}>Payout History</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {[
                { date: 'Aug 01, 2026', amount: '$3,420.00', status: 'Completed', method: 'Stripe Express (•••• 4821)' },
                { date: 'Jul 15, 2026', amount: '$2,850.00', status: 'Completed', method: 'Stripe Express (•••• 4821)' },
                { date: 'Jul 01, 2026', amount: '$1,980.00', status: 'Completed', method: 'Stripe Express (•••• 4821)' }
              ].map((p, idx) => (
                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: 'var(--bg-base)', borderRadius: '12px', border: '1px solid var(--border-subtle)' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.875rem' }}>{p.amount}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{p.date} · {p.method}</div>
                  </div>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--success)', background: 'var(--success-subtle)', padding: '3px 8px', borderRadius: '999px' }}>
                    {p.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tab 4: Live Activity */}
      {activeTab === 'activity' && (
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: '20px', padding: '28px', maxWidth: '720px' }}>
          <h3 style={{ fontSize: '1.2rem', fontWeight: 800, marginBottom: '20px' }}>Collector & Follower Events</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {recentActivity.map(act => (
              <div key={act.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', background: 'var(--bg-base)', borderRadius: '14px', border: '1px solid var(--border-subtle)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: 'var(--primary-subtle)',
                    color: 'var(--primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {act.type === 'sale' ? <DollarSign size={18} /> : act.type === 'download' ? <Download size={18} /> : <Star size={18} />}
                  </div>
                  <div>
                    <div style={{ fontSize: '0.9375rem', color: 'var(--text-primary)' }}>
                      <strong>{act.user}</strong> {act.type === 'sale' ? 'purchased' : act.type === 'download' ? 'downloaded' : 'rated'} <span style={{ color: 'var(--primary-text)', fontWeight: 600 }}>{act.item}</span>
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>{act.time}</div>
                  </div>
                </div>
                {act.amount && (
                  <span style={{ fontSize: '0.9375rem', fontWeight: 800, color: 'var(--success)' }}>{act.amount}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modals */}
      <ShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        title={selectedTemplate ? `Share ${selectedTemplate.title}` : 'Share Template'}
      />

      <DesignerOnboarding 
        isOpen={onboardingOpen} 
        onClose={() => setOnboardingOpen(false)} 
        onSuccess={() => window.location.reload()} 
      />

      {/* Boost & Feature Placement Modal */}
      {boostModalOpen && boostingTemplate && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(5, 5, 10, 0.85)',
          backdropFilter: 'blur(12px)',
          zIndex: 1300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
          animation: 'fadeIn 0.2s ease'
        }} onClick={() => setBoostModalOpen(false)}>
          <div style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-medium)',
            borderRadius: '24px',
            width: '100%',
            maxWidth: '520px',
            padding: '28px',
            boxShadow: 'var(--shadow-xl)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ padding: '8px', borderRadius: '12px', background: 'var(--primary-subtle)', color: 'var(--primary)' }}>
                  <Zap size={20} />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 800 }}>Boost Asset Placement</h3>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Maximize impressions & purchases</span>
                </div>
              </div>
              <button onClick={() => setBoostModalOpen(false)} style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            {/* Template Info Card */}
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', padding: '12px', background: 'var(--bg-base)', borderRadius: '14px', border: '1px solid var(--border-subtle)', marginBottom: '20px' }}>
              <img src={boostingTemplate.thumbnail} alt={boostingTemplate.title} style={{ width: '56px', height: '56px', borderRadius: '10px', objectFit: 'cover' }} />
              <div>
                <strong style={{ fontSize: '0.9375rem', color: 'var(--text-primary)', display: 'block' }}>{boostingTemplate.title}</strong>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Current Price: {boostingTemplate.price === 0 ? 'Free' : `$${boostingTemplate.price}`}</span>
              </div>
            </div>

            <form onSubmit={handleApplyBoost} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <label style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>
                  Select Spotlight Placement Slot
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                  {[
                    { id: 'hero', label: 'Hero Carousel', mult: '3.5x Reach' },
                    { id: 'trending', label: 'Trending Feed', mult: '2.2x Reach' },
                    { id: 'category', label: 'Category Top', mult: '1.8x Reach' }
                  ].map(slot => (
                    <button
                      key={slot.id}
                      type="button"
                      onClick={() => setBoostSlot(slot.id)}
                      className="tactile-btn"
                      style={{
                        padding: '12px 8px',
                        borderRadius: '12px',
                        background: boostSlot === slot.id ? 'var(--primary-subtle)' : 'var(--bg-base)',
                        border: `1.5px solid ${boostSlot === slot.id ? 'var(--primary)' : 'var(--border-subtle)'}`,
                        color: boostSlot === slot.id ? 'var(--primary-text)' : 'var(--text-primary)',
                        cursor: 'pointer',
                        textAlign: 'center'
                      }}
                    >
                      <div style={{ fontSize: '0.8125rem', fontWeight: 700 }}>{slot.label}</div>
                      <div style={{ fontSize: '0.6875rem', color: boostSlot === slot.id ? 'var(--primary)' : 'var(--accent)', fontWeight: 700, marginTop: '2px' }}>{slot.mult}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>
                  Boost Campaign Duration
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                  {[
                    { days: 3, price: '$15' },
                    { days: 7, price: '$29' },
                    { days: 14, price: '$49' }
                  ].map(plan => (
                    <button
                      key={plan.days}
                      type="button"
                      onClick={() => setBoostDuration(plan.days)}
                      className="tactile-btn"
                      style={{
                        padding: '12px 8px',
                        borderRadius: '12px',
                        background: boostDuration === plan.days ? 'var(--primary-subtle)' : 'var(--bg-base)',
                        border: `1.5px solid ${boostDuration === plan.days ? 'var(--primary)' : 'var(--border-subtle)'}`,
                        color: boostDuration === plan.days ? 'var(--primary-text)' : 'var(--text-primary)',
                        cursor: 'pointer',
                        textAlign: 'center'
                      }}
                    >
                      <div style={{ fontSize: '0.9375rem', fontWeight: 800 }}>{plan.days} Days</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{plan.price} total</div>
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px' }}>
                <Button variant="ghost" type="button" onClick={() => setBoostModalOpen(false)}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={boosting}>
                  {boosting ? 'Activating Boost...' : '🚀 Launch Boost Campaign'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
